import Web3 from "web3";
import { getUserInfo, getNodeHasReward } from "@/contract";
import { get_user_info_api, get_referral_list } from "@/api";
import {
  formatScaledAmountDisplay,
  formatAmountDisplay,
  coerceStringZero,
} from "@/utils/utils.js";
import {
  parseUserInfoApiResponse,
  parseReferralListResponse,
  levelLabel,
} from "./homeUserApi.js";

/** 合约中与业绩、奖励相关的 USDT（Purchase 计价币）精度 */
export const USDT_DECIMALS = 6;

/** 宾利链上代币等 ERC20 常用精度 */
export const TOKEN_18_DECIMALS = 18;

function tupleFour(res) {
  if (res == null) return ["0", "0", "0", "0"];
  return [0, 1, 2, 3].map((i) => res[i] ?? res[String(i)] ?? "0");
}

function fmtUsdt(raw) {
  const s = Web3.utils.toBN(String(raw ?? 0)).toString(10);
  const v = formatScaledAmountDisplay(1, BigInt(s), USDT_DECIMALS, 3);
  return `${v} USDT`;
}

function fmtBl(raw) {
  const s = Web3.utils.toBN(String(raw ?? 0)).toString(10);
  const v = formatScaledAmountDisplay(1, BigInt(s), TOKEN_18_DECIMALS, 3);
  return `${v} BL`;
}

function fmtUsdtFromApi(value) {
  return `${formatAmountDisplay(value, 3)} USDT`;
}

/** 奖励未提取 = API 总业绩 × 0.03 − 链上已提取节点奖励（USDT） */
function fmtUnclaimedNodeReward(performApi, nodeHasRewardRaw) {
  const performNum = Number(coerceStringZero(performApi));
  if (!Number.isFinite(performNum)) {
    return `${formatAmountDisplay(0, 3)} USDT`;
  }
  const expectedUsdt = performNum * 0.03;
  const withdrawnStr = formatScaledAmountDisplay(
    1,
    BigInt(Web3.utils.toBN(String(nodeHasRewardRaw ?? 0)).toString(10)),
    USDT_DECIMALS,
    3
  );
  const withdrawnUsdt = Number(withdrawnStr);
  const unclaimed = expectedUsdt - (Number.isFinite(withdrawnUsdt) ? withdrawnUsdt : 0);
  return `${formatAmountDisplay(unclaimed, 3)} USDT`;
}

/**
 * 拉取用户页「个人业绩」与「直推列表」（链上 getUserInfo + 后端 API）
 * @returns {{ personalStats: Array<{value:string,label:string}>, directReferrals: string[] }}
 */
export async function fetchHomePersonalAndReferrals(walletAddress) {
  if (!walletAddress) {
    const err = new Error("请先连接钱包");
    err.code = "NO_CONTRACT_OR_WALLET";
    throw err;
  }

  const [chainInfo, apiRes, referralRes, nodeHasRewardRaw] = await Promise.all([
    getUserInfo(walletAddress),
    get_user_info_api({ wallet: walletAddress }),
    get_referral_list({ wallet: walletAddress }),
    getNodeHasReward(walletAddress),
  ]);

  const { perform, community, level } = parseUserInfoApiResponse(apiRes);
  const directReferrals = parseReferralListResponse(referralRes);
  const [shareReward, levelReward, nodeIdx, nodeReward] = tupleFour(chainInfo);

  console.log("[display/raw] 个人业绩/奖励 — 链上与 API 原始值", {
    walletAddress,
    getUserInfo_raw: chainInfo,
    get_user_info_api_raw: apiRes?.data,
    get_referral_list_raw: referralRes?.data,
    parsed: {
      directLen: directReferrals.length,
      shareReward_raw_usdt6: Web3.utils.toBN(String(shareReward)).toString(10),
      levelReward_raw_bl18: Web3.utils.toBN(String(levelReward)).toString(10),
      nodeIdx_raw: Web3.utils.toBN(String(nodeIdx)).toString(10),
      nodeReward_raw_bl18: Web3.utils.toBN(String(nodeReward)).toString(10),
      nodeHasReward_raw_usdt6: Web3.utils
        .toBN(String(nodeHasRewardRaw))
        .toString(10),
      perform_api: perform,
      community_api: community,
      level_api: level,
    },
    referralCount: directReferrals.length,
  });

  const personalStats = [
    {
      value: String(directReferrals.length),
      label: "直推数量",
    },
    { value: fmtUsdtFromApi(perform), label: "总业绩" },
    { value: fmtUsdtFromApi(community), label: "小区业绩" },
    { value: fmtUsdt(shareReward), label: "推荐奖励" },
    { value: levelLabel(level), label: "个人级别" },
    { value: fmtBl(levelReward), label: "级别奖励" },
    {
      value: Web3.utils.toBN(String(nodeIdx)).gt(Web3.utils.toBN(0))
        ? "是"
        : "否",
      label: "节点",
    },
    { value: fmtBl(nodeReward), label: "节点奖励" },
    {
      value: fmtUnclaimedNodeReward(perform, nodeHasRewardRaw),
      label: "奖励未提取",
    },
  ];

  return {
    personalStats,
    directReferrals,
    isNode: Web3.utils.toBN(String(nodeIdx)).gt(Web3.utils.toBN(0)),
  };
}
